<template>
    <div>
      <div class="row">
        <div class="col-12 mb-4 d-flex flex-row align-items-center" style="gap: 0.5rem">
          <h1 class="mr-auto">{{ id ? $t('edit-event') : $t('new-event') }}</h1>
          <button
            v-if="id"
            :class="`btn btn-outline-${bulkEdit ? 'primary' : 'secondary'}`"
            @click="bulkEdit = !bulkEdit"
          >
            {{ $t('edit-many') }} <b-icon icon="pencil" class="ml-1" />
          </button>
          <button
            :disabled="loading || submitting"
            type="button"
            class="btn btn-primary"
            @click="showSaveDialog"
          >
            {{ id ? $t('save') : $t('create') }}
          </button>
          <button
            v-if="id"
            :disabled="loading || submitting"
            type="button"
            class="btn btn-outline-danger"
            @click="showDeleteDialog"
          >
            {{ $t('delete') }}
          </button>
        </div>
        <div v-if="saved" class="col-12 mb-2">
          <div class="alert alert-success">{{ $t('event-saved') }}</div>
        </div>
        <div v-else-if="error.errors" class="col-12 mb-2">
          <div class="alert alert-danger">{{ $t('missing-required-fields') }}</div>
        </div>
  
        <div v-if="!loading" class="col-12 mb-2">
          <div class="list-group border-radius-2 no-border mb-4">
            <div class="list-group-item d-flex flex-row align-items-center flex-wrap" style="gap: 1rem">
              <DateInput
                v-show="!bulkEdit"
                v-model="date"
                :label="$t('date')"
                class="flex-grow-1"
                :min="timetable.datefrom"
                :max="timetable.dateto"
                :error="error.errors && error.date"
                @input="error.date = false"
              />
              <TimeInput
                v-model="from"
                :label="$t('from')"
                class="flex-grow-1"
                :error="error.errors && error.from"
                @input="error.from = false"
              />
              <TimeInput
                v-model="to"
                :label="$t('until')"
                class="flex-grow-1"
                :error="error.errors && error.to"
                @input="error.to = false"
              />
            </div>
            <div class="list-group-item d-flex flex-row flex-wrap align-items-center" style="gap: 1rem">
              <div style="flex: 1 1 250px; min-width: 250px;">
                <label for="select-ttsessiontype" class="form-label mb-1">
                  {{ $t('session-type') }}
                </label>
                <SelectSearch
                  v-model="event.ttsessiontype"
                  @input="error.ttsessiontype = false"
                  :options="sessiontypes"
                  optionLabel="name"
                  style="min-width: 250px"
                  :error="error.errors && error.ttsessiontype"
                />
              </div>

              <div v-if="event.ttsessiontype && event.ttsessiontype.ttrooms.length > 0" style="flex: 2 1 300px; min-width: 300px;" >
                <label for="select-ttroom" class="form-label mb-1">
                  {{ $t('room') }}
                </label>
                <SelectSearch
                  id="select-ttroom"
                  v-model="event.ttroom"
                  @input="error.ttroom = false"
                  :options="event.ttsessiontype.ttrooms"
                  optionLabel="name"
                  style="min-width: 300px"
                  :error="error.errors && error.ttroom"
                />
              </div>
            </div>
            <div class="list-group-item">
              <TextInput v-model="event.title" :label="$t('title')" />
            </div>
            <div v-show="!bulkEdit" class="list-group-item">
              <TextAreaInput v-model="event.ttevent_notes" :label="$t('notes')" rows="2" />
            </div>
          </div>
  
          <div v-if="bulkEdit" class="list-group border-radius-2 no-border mb-2">
            <div class="list-group-item font-weight-bold d-flex align-items-center">
              <span class="mr-4">{{ $t('select-events-to-update') }}</span>
              <YesNoToggle
                v-model="showContiguous"
                class="mr-auto my-0"
                :yesLabel="$t('this-week')"
                :noLabel="$t('weekly')"
                :noFirst="true"
                :label="$t('match-weekly-or-this-week')"
                :srOnly="true"
                @input="updateIds = []"
              />
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click="updateIds = (updateIds.length ? [] : (showContiguous ? contiguousEvents : similarEvents).map(e => e.id))"
              >
                {{ $t('toggle-all') }}
              </button>
            </div>
            <div class="list-group-item d-flex flex-wrap align-items-center" style="gap: 0.5rem">
              <div
                v-for="similar in (showContiguous ? contiguousEvents : similarEvents)"
                :key="similar.id"
                class="rounded border shadow-sm p-2 d-flex flex-column"
                :class="{ 'bg-light border-dark': updateIds.includes(similar.id) }"
                style="gap: 0.25rem; cursor: pointer;"
                @click="toggleUpdateId(similar.id)"
              >
                <span>{{ $datetime.timestampToDay(similar.from) }}</span>
                <span>{{ $datetime.getLocaleDateStringFromTimestamp(similar.from) }}</span>
                <TimetableEvent :event="similar" class="p-1" />
              </div>
            </div>
          </div>
        </div>
  
        <template v-if="!bulkEdit">
          <div v-if="clashes.length || teacherClashes.length" class="col-12 mb-2" :class="{ 'col-md-6': capacity }">
            <div class="list-group border-radius-2 no-border mb-4">

              <!-- Student Clashes -->
              <template v-if="clashes.length">
                <div class="list-group-item list-group-item-danger text-sm font-weight-bold">
                  {{ $t('clashes') }}
                  <b-spinner small
                    v-if="condoning"
                    class="float-right"
                    variant="primary" 
                    type="grow"
                    label="Condoning" 
                />
                </div>
                <div
                  v-for="clash in clashes"
                  :key="`student-${clash.user_id}-${clash.ttevent_id}`"
                  class="list-group-item d-flex align-items-center"
                  :class="clash.condoned == '1' ? 'list-group-item-primary' : 'list-group-item-danger'"
                >
                  <span class="mr-auto">{{ clash.displayname }}</span>
                  <button
                    class="btn btn-sm btn-danger"
                    @click="$router.push(`/timetables/${clash.tttimetable_id}/events/${clash.ttevent_id}`)"
                  >
                    {{ $t('view-event') }}
                    <b-icon icon="arrow-right" class="ml-2" />
                  </button>
                  <button v-if="canCondone"
                    class="btn btn-sm ml-1"
                    @click="condone(clash.user_id , clash.ttevent_id, clash.condoned == '1')"
                    :class="clash.condoned == '1' ? 'btn-secondary px-2' : 'btn-success'"
                  >
                    <template v-if="clash.condoned !== '1'">
                        {{ $t('ignore') }} <b-icon icon="check2" class="ml-2" />
                    </template>
                    <template v-else>
                        {{ $t('undo') }} <b-icon icon="arrow-counterclockwise" class="ml-3" />
                    </template>
                  </button>
                </div>
              </template>

              <!-- Teacher Clashes -->
              <template v-if="teacherClashes.length">
                <div class="list-group-item list-group-item-danger text-sm font-weight-bold">
                  {{ $t('teacher-clashes') }}
                </div>
                <div
                  v-for="clash in teacherClashes"
                  :key="`teacher-${clash.user_id}-${clash.ttevent_id}`"
                  class="list-group-item d-flex align-items-center"
                  :class="clash.condoned == '1' ? 'list-group-item-primary' : 'list-group-item-danger'"

                >
                  <span class="mr-auto">{{ clash.displayname }}</span>
                  <button
                    class="btn btn-sm btn-danger"
                    @click="$router.push(`/timetables/${clash.tttimetable_id}/events/${clash.ttevent_id}`)"
                  >
                    {{ $t('view-event') }}
                    <b-icon icon="arrow-right" class="ml-2" />
                  </button>
                  <button v-if="canCondone"
                    class="btn btn-sm ml-1"
                    @click="condone(clash.user_id , clash.ttevent_id, clash.condoned == '1',true)"
                    :class="clash.condoned == '1' ? 'btn-secondary px-2' : 'btn-success'"
                  >
                    <template v-if="clash.condoned !== '1'">
                        {{ $t('ignore') }} <b-icon icon="check2" class="ml-2" />
                    </template>
                    <template v-else>
                        {{ $t('undo') }} <b-icon icon="arrow-counterclockwise" class="ml-3" />
                    </template>
                  </button>
                </div>
              </template>

            </div>
          </div>

          <!-- Over Capacity -->
          <div
            v-if="capacity && Number(capacity.capacity) && capacity.capacity < capacity.attending"
            class="col-12 mb-2"
            :class="{ 'col-md-6': (clashes.length || teacherClashes.length) && capacity }"
          >
            <div class="list-group border-radius-2 no-border mb-4">
              <div class="list-group-item list-group-item-info text-sm font-weight-bold">
                {{
                  $t('over-capacity-msg', {
                    n: capacity.attending,
                    m: capacity.capacity,
                    o: capacity.events.length - (id ? 1 : 0)
                  })
                }}
                <span v-if="capacity.events.length - (id ? 1 : 0)">
                  {{ $tc('over-capacity-events', capacity.events.length - (id ? 1 : 0)) }}
                </span>
              </div>
              <div
                v-for="ev in capacity.events.filter(e => e.id != id)"
                :key="ev.id"
                class="list-group-item list-group-item-info d-flex align-items-center"
              >
                {{ $t('event-with-n-students', { n: ev.attending }) }}
                <button
                  type="button"
                  class="btn btn-sm btn-info ml-auto"
                  @click="$router.push(`/timetables/${ev.tttimetable_id}/events/${ev.id}`)"
                >
                  {{ $t('view-event') }}
                  <b-icon icon="arrow-right" class="ml-2" />
                </button>
              </div>
            </div>
          </div>
          <div v-if="!loading && !id" class="col-12 mb-4">
            <RecurEventSettings :event="event" />
          </div>
        </template>
      </div>
  
      <div v-if="!loading && !bulkEdit" class="row flex-lg-row-reverse">
        <div class="col-12 col-lg-6">
          <div class="list-group border-radius-2 no-border mb-4">
            <div class="list-group-item">
              <EventTeacherEditor v-model="event.teachers" />
            </div>
          </div>
        </div>
  
        <div class="col-12 col-lg-6">
          <div class="list-group border-radius-2 no-border mb-4">
            <div class="list-group-item">
              <EventStudentEditor v-model="event.students" :options="timetable.students" />
            </div>
          </div>
        </div>
  
        <div class="col-12 mb-4 d-flex flex-row align-items-center">
          <button
            :disabled="loading || submitting"
            type="button"
            class="btn btn-primary ml-auto"
            @click="showSaveDialog"
          >
            {{ id ? $t('save') : $t('create') }}
          </button>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { dateFromISODate, dateToISODate } from "src/modules/timetable/util";
  import SessionManager from "src/modules/session/sessionManager";
  import DateInput from "src/components/Inputs/DateInput.vue";
  import TimeInput from "src/components/Inputs/TimeInput.vue";
  import TextAreaInput from "src/components/Inputs/TextAreaInput.vue";
  import SelectSearch from "src/components/Inputs/SelectSearch.vue";
  import EventStudentEditor from "src/components/Events/EventStudentEditor.vue";
  import EventTeacherEditor from "src/components/Events/EventTeacherEditor.vue";
  import RecurEventSettings from "src/components/Timetables/RecurEventSettings.vue";
  import TimetableEvent from "src/components/Timetables/TimetableEvent.vue";
  import YesNoToggle from "src/components/Inputs/YesNoToggle.vue";
  //import RoomSelector from "src/components/Rooms/RoomSelector";
  
  export default {
    components: {
      DateInput,
      TimeInput,
      TextAreaInput,
      SelectSearch,
      EventStudentEditor,
      EventTeacherEditor,
      RecurEventSettings,
      TimetableEvent,
      YesNoToggle,
      //RoomSelector
    },
    data() {
      return {
        loading: true,
        submitting: false,
        timetable: null,
        id: Number(this.$route.params.eventId),
        event: {
          ttsessiontype: null,
          ttroom: null,
          title: '',
          ttevent_notes: '',
          students: [],
          teachers: [],
        },
        saved: false,
        date: null,
        from: null,
        to: null,
        sessiontypes: [],
        showOptions: false,
        notifyStudents: false,
        notifyProvider: false,
        error: {
          errors: false,
          date: false,
          from: false,
          to: false,
          ttsessiontype: false,
        },
        createoradd: false,
        clashes: [],
        teacherClashes: [],
        capacity: null,
        similarEvents: [],
        contiguousEvents: [],
        showContiguous: false,
        bulkEdit: false,
        updateIds: [],
        condoning: false,
  
        sessionManager: new SessionManager(this.$route)
      };
    },

    mounted() {
      Promise.all([this.fetchTimetable(), this.fetchEvent(), this.fetchSessions()]).then(() => {
        if (this.id) {
          Promise.all([this.fetchSimilar(), this.fetchContiguous()]).then(() => {
            const state = this.sessionManager.get('state');
            if (state) {
              this.bulkEdit = state.bulkEdit;
              this.showContiguous = state.showContiguous;
            } else {
              this.bulkEdit = false;
              this.showContiguous = false;
            }
            this.sessionManager.set('state', {});
            this.loading = false;
          });
        } else {
          this.loading = false;
        }
  
        this.event.ttsessiontype = this.sessiontypes.filter(el => el.id == this.event.ttsessiontype_id)[0];
        const ttroom_id = this.event.ttroom ? this.event.ttroom.id : undefined;
  
        if (ttroom_id) {
          this.event.ttroom = this.event.ttsessiontype.ttrooms.find(r => r.id == ttroom_id);
        }
  
        if (this.id) {
          if (this.event.students?.length) {
            this.fetchClashes();
          }
          if (this.event.teachers?.length) {
            this.fetchTeacherClashes();
          }
          this.fetchCapacity();
        }
      });
    },
    methods: {
      fetchTimetable() {
        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/get`, {
          method: 'GET',
          data: { id: this.$route.params.timetableId },
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw new Error();
  
            this.timetable = res.result;
            this.timetable.students = this.timetable.students.map(student => {
              return {
                ...student,
                id: student.user_id,
              };
            });
          })
          .catch(err => {
            console.error(err);
            this.$log.error.call(this, err);
            this.$store.commit('openDialog', {
              title: this.$t('something-went-wrong'),
              content: this.$t('error-fetching-timetable'),
              confirmCallBack: () => {
                this.$router.push('/');
              }
            });
          });
      },
      fetchEvent() {
        if (!this.id) return Promise.resolve();
  
        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/get`, {
          method: 'GET',
          data: { id: this.id },
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw new Error();
  
            this.event = res.result;
            this.event.teachers = (res.result.teachers || []).filter(t => t && (t.id || t.user_id)).map(t => ({
              ...t,
              id: Number(t.id || t.user_id)
            }));
            this.date = this.event.ttevent_datetimefrom.split(' ')[0];
            this.from = this.event.ttevent_datetimefrom.split(' ')[1];
            this.to = this.event.ttevent_datetimeto.split(' ')[1];
          })
          .catch(err => {
            this.$log.error.call(this, err);
            this.$store.commit('openDialog', {
              title: this.$t('something-went-wrong'),
              content: this.$t('error-fetching-event'),
              confirmCallBack: () => {
                this.$router.push('/');
              }
            });
          });
      },
      fetchSessions() {
        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/sessiontypes/all`, {
          method: 'GET',
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw new Error();
  
            this.sessiontypes = res.results;
          })
          .catch(err => {
            this.$log.error.call(this, err);
            this.$store.commit('openDialog', {
              title: this.$t('something-went-wrong'),
              content: this.$t('error-fetching-session-types'),
            });
          });
      },
      fetchSimilar() {
        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/all`, {
          method: 'POST',
          data: {
            timetable_id: this.timetable.id,
            from: this.date,
            to: this.timetable.dateto,
            start: this.from,
            end: this.to,
            ttsessiontype_id: this.event.ttsessiontype_id,
            ttroom_id: this.event.ttroom_id,
            title: this.title,
            dayofweek: dateFromISODate(this.date).getDay() + 1,
          }
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw new Error(res);
  
            this.similarEvents = res.results.map(ev => {
              ev.ttsessiontype = this.sessiontypes.find(st => st.id == ev.ttsessiontype_id);
              ev.ttroom = ev.ttsessiontype.ttrooms.find(r => r.id == ev.ttroom_id);
              return ev;
            });
          })
          .catch(err => {
            console.error(err);
            this.$log.error.call(this, err);
          });
      },
      fetchContiguous() {
        const date = dateFromISODate(this.date);
        date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  
        const from = dateToISODate(date);
        date.setDate(date.getDate() + 6);
        const to = dateToISODate(date);
  
        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/all`, {
          method: 'POST',
          data: {
            timetable_id: this.timetable.id,
            from,
            to,
            ttsessiontype_id: this.event.ttsessiontype_id,
            ttroom_id: this.event.ttroom_id,
            title: this.event.title,
          }
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw new Error(res);
  
            this.contiguousEvents = res.results.map(ev => {
              ev.ttsessiontype = this.sessiontypes.find(st => st.id == ev.ttsessiontype_id);
              ev.ttroom = ev.ttsessiontype.ttrooms.find(r => r.id == ev.ttroom_id);
              return ev;
            });
          })
          .catch(err => {
            console.error(err);
            this.$log.error.call(this, err);
          });
      },
      async saveEvent() {
        if (this.submitting) return Promise.resolve();
  
        document.getElementById('main').scrollTo({ top: 0 });
  
        this.error = {
          errors: !this.date || !this.from || !this.to || !this.event.ttsessiontype,
          date: !this.date,
          from: !this.from,
          to: !this.to,
          ttsessiontype: !this.event.ttsessiontype,
        };
  
        if (this.error.errors) return Promise.resolve();
  
        if (this.alertIfNoStudents()) return;

        this.$store.commit('showBlockingSpinner');
  
        await Promise.all([this.fetchClashes(), this.fetchTeacherClashes(), this.fetchCapacity()]).finally(() => {
          this.$store.commit('hideBlockingSpinner');
        });
  
        let clashRes;
        let clashRej;
        const clashPromise = new Promise((res, rej) => {
          clashRes = res;
          clashRej = rej;
        });
  
        let capRes;
        let capRej;
        const capPromise = new Promise((res, rej) => {
          capRes = res;
          capRej = rej;
        });
  
        if (this.clashes.length) {
          this.$store.commit('openDialog', {
            title: this.$t('clashes-found'),
            content: this.$t('clashes-found-msg'),
            confirmCallBack: clashRes,
            cancelCallBack: clashRej,
            cancelBtn: true,
            confirmBtnText: this.$t('save-anyway'),
            cancelBtnText: this.$t('view-clashes'),
          });
        } else {
          clashRes();
        }
  
        await clashPromise.catch();
  
        if (this.teacherClashes.length) {
          let teachRes, teachRej;
          const teachPromise = new Promise((res, rej) => { teachRes = res; teachRej = rej; });
          this.$store.commit('openDialog', {
            title: this.$t('teacher-clashes-found'),
            content: this.$t('teacher-clashes-found-msg'),
            confirmBtnText: this.$t('save-anyway'),
            cancelBtnText: this.$t('view-clashes'),
            confirmCallBack: teachRes,
            cancelCallBack: teachRej,
            cancelBtn: true,
          });
          await teachPromise.catch();
        }
  
        if (this.capacity && Number(this.capacity.capacity) && this.capacity.capacity < this.capacity.attending) {
          this.$store.commit('openDialog', {
            title: this.$t('over-capacity'),
            content:
              this.$t('over-capacity-msg', {
                n: this.capacity.attending,
                m: this.capacity.capacity,
                o: this.capacity.events.length - (this.id ? 1 : 0)
              }) +
              ((this.capacity.events.length - (this.id ? 1 : 0) > 0)
                ? ` ${this.$tc('over-capacity-events', this.capacity.events.length - (this.id ? 1 : 0))}`
                : ''),
            confirmCallBack: capRes,
            cancelCallBack: capRej,
            cancelBtn: true,
            confirmBtnText: this.$t('save-anyway'),
            cancelBtnText: this.$t('back'),
          });
        } else {
          capRes();
        }
  
        await capPromise.catch();
  
        this.$store.commit('showBlockingSpinner');
        this.submitting = true;
        this.saved = false;
  
        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/save`, {
          method: 'POST',
          data: {
            createoradd: this.createoradd,
            id: this.id ? this.id : undefined,
            tttimetable_id: this.timetable.id,
            ttsessiontype_id: this.event.ttsessiontype.id,
            ttroom_id: this.event.ttroom ? this.event.ttroom.id : undefined,
            title: this.event.title,
            notes: this.event.ttevent_notes,
            date: this.date,
            from: this.from,
            to: this.to,
            students: this.event.students,
            teachers: this.event.teachers,
            notify: this.notifyStudents,
            notify_provider: this.notifyProvider,
            recur_count: this.event.recur_count,
            recur_period: this.event.recur_period,
          },
        })
          .then(res => {
            if (res.status == 'SUCCESS') {
              this.saved = true;
              this.createoradd = false;
  
              if (!this.id) {
                this.$router.push(`/timetables/edit/${this.timetable.id}?from=${this.date}`);
              }
            } else {
              if (res.errorType && res.errorType == 'EVENT_CONFLICT') {
                var self = this;
                this.$store.commit('openDialog', {
                  title: this.$t('found-matching-events'),
                  content: this.$t('matching-events-in-timetable'),
                  cancelBtn: true,
                  cancelBtnText: this.$t("update-existing-events"),
                  confirmBtnText: this.$t("cancel"),
                  confirmCallBack: function () {},
                  cancelCallBack: function () {
                    self.createoradd = true;
                    self.saveEvent();
                  }
                });
              } else {
                this.$store.commit('openDialog', {
                  title: this.$t('something-went-wrong'),
                  content: this.$t('error-saving-event'),
                });
              }
            }
          })
          .catch(err => {
            console.error(err);
            this.$log.error.call(this, err);
            this.$store.commit('openDialog', {
              title: this.$t('something-went-wrong'),
              content: this.$t('error-saving-event'),
            });
          })
          .finally(() => {
            this.$store.commit('hideBlockingSpinner');
            this.createoradd = false;
            this.submitting = false;
          });
      },
      bulkSave() {
        if (this.submitting) return;
        this.submitting = true;
  
        this.$store.commit('showBlockingSpinner');
  
        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/bulkupdate`, {
          method: 'POST',
          data: {
            ttevent_ids: this.updateIds,
            from: this.from,
            to: this.to,
            ttsessiontype_id: this.event.ttsessiontype.id,
            ttroom_id: this.event.ttroom ? this.event.ttroom.id : undefined,
            title: this.event.title,
            notify: this.notifyStudents,
          },
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw new Error(res);
  
            this.sessionManager.set('state', {
              bulkEdit: this.bulkEdit,
              showContiguous: this.showContiguous,
            });
  
            location.reload();
          })
          .catch(err => {
            console.error(err);
            this.$log.error.call(this, err);
          })
          .finally(() => {
            this.submitting = false;
            this.$store.commit('hideBlockingSpinner');
          });
      },
      bulkDelete() {
        if (this.submitting) return;
        this.submitting = true;
  
        this.$store.commit('showBlockingSpinner');
  
        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/bulkdelete`, {
          method: 'POST',
          data: {
            ttevent_ids: this.updateIds,
            notify: this.notifyStudents,
          },
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw new Error(res);
  
            this.$router.go(-1);
          })
          .finally(() => {
            this.$store.commit('hideBlockingSpinner');
            this.submitting = false;
          });
      },
      fetchClashes() {
        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/clash`, {
          method: 'POST',
          data: {
            timetable_id: this.timetable.id,
            from: `${this.date} ${this.from}`,
            to: `${this.date} ${this.to}`,
            user_ids: this.event.students.map(st => st.id),
            event_id: this.id ? this.id : undefined,
          },
        })
          .then(res => {
            if (res.status !== 'SUCCESS') {
              this.$store.commit('openDialog', {
                title: this.$t('something-went-wrong'),
                content: this.$t('error-checking-clashes')
              });
              return;
            }
  
            this.clashes = res.results;
          })
          .catch(err => {
            this.$log.error.call(this, err);
            this.$store.commit('openDialog', {
              title: this.$t('something-went-wrong'),
              content: this.$t('error-checking-clashes')
            });
          });
      },
      fetchTeacherClashes() {
        const teacherIds = this.event.teachers
          .filter(t => t && t.id != null)
          .map(t => Number(t.id))
          .filter(id => !isNaN(id));      

        if (!teacherIds.length) return;


        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/clash`, {
          method: 'POST',
          data: {
            timetable_id: this.timetable.id,
            from: `${this.date} ${this.from}`,
            to: `${this.date} ${this.to}`,
            user_ids: teacherIds,
            event_id: this.id || undefined,
          },
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw new Error();
            this.teacherClashes = res.results;
          })
          .catch(err => {
            console.error(err);
            this.$store.commit('openDialog', {
              title: this.$t('something-went-wrong'),
              content: this.$t('error-checking-clashes'),
            });
          });
      },
      fetchCapacity() {
        if (!this.event.ttroom) return Promise.resolve();
  
        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/rooms/capacity`, {
          method: 'GET',
          data: {
            ttroom_id: this.event.ttroom.id,
            from: `${this.date} ${this.from}`,
            to: `${this.date} ${this.to}`,
          }
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw res;
  
            if (!res.result.capacity) {
              this.capacity = {
                attending: this.event.students.length,
                capacity: Number(this.event.ttroom.capacity),
                events: [],
              };
  
              return;
            }
  
            this.capacity = res.result;
            if (!this.id) this.capacity.attending += this.event.students.length;
          })
          .catch(err => {
            this.$log.error.call(this, err);
          });
      },
      async showDeleteDialog() {
        let deleteRes, deleteRej;
        const deletePromise = new Promise((res, rej) => {
          deleteRes = res;
          deleteRej = rej;
        });
  
        this.$store.commit('openDialog', {
          title: this.$t('are-you-sure'),
          content: this.$t('are-you-sure-delete-event'),
          cancelBtn: true,
          confirmCallBack: deleteRes,
          cancelCallBack: deleteRej,
          confirmBtnText: this.$t('delete'),
        });
  
        await deletePromise.catch();
  
        let notifyRes;
        const notifyPromise = new Promise(res => {
          notifyRes = res;
        });
  
        this.$store.commit('openDialog', {
          title: this.$t('deleting-event'),
          content: this.$t('notify-students-question'),
          cancelBtn: true,
          confirmCallBack: () => {
            this.notifyStudents = true;
            notifyRes();
          },
          confirmBtnText: this.$t('notify'),
          cancelBtnText: this.$t('no'),
          cancelCallBack: notifyRes,
        });
  
        notifyPromise.then(() => {
          (this.bulkEdit ? this.bulkDelete() : this.deleteEvent()).finally(() => {
            this.notifyStudents = false;
          });
        });
      },
      async showSaveDialog() {
        if (this.bulkEdit) {
          this.showBulkSaveDialog();
          return;
        }

        if (this.alertIfNoStudents()) return;

        if (this.isOutsideTimetableSpan) {
          let spanRes, spanRej;
          const spanPromise = new Promise((res, rej) => { spanRes = res; spanRej = rej; });
          this.$store.commit('openDialog', {
            title: this.$t('event-outside-timetable-span'),
            content: this.$t('event-outside-timetable-span-msg', { from: this.timetable.datefrom, to: this.timetable.dateto }),
            cancelBtn: true,
            confirmBtnText: this.$t('save-anyway'),
            cancelBtnText: this.$t('back'),
            confirmCallBack: spanRes,
            cancelCallBack: spanRej,
          });
          try { await spanPromise; } catch { return; }
        }

        if (this.timetable.status != 2) {
          this.saveEvent();
          return;
        }

        await new Promise(res => {
          this.$store.commit('openDialog', {
            title: this.$t('saving-event'),
            content: this.$t('notify-students-question'),
            cancelBtn: true,
            confirmCallBack: () => {
              this.notifyStudents = true;
              res();
            },
            confirmBtnText: this.$t('notify'),
            cancelBtnText: this.$t('no'),
            cancelCallBack: res
          });
        });

        await new Promise(res => {
          this.$store.commit('openDialog', {
            title: this.$t('saving-event'),
            content: this.$t('notify-provider-question'),
            cancelBtn: true,
            confirmCallBack: () => {
              this.notifyProvider = true;
              res();
            },
            confirmBtnText: this.$t('notify'),
            cancelBtnText: this.$t('no'),
            cancelCallBack: res
          });
        });

        this.saveEvent().finally(() => {
          this.notifyStudents = false;
          this.notifyProvider = false;
        });
      },
      async showBulkSaveDialog() {
        let saveRes, saveRej;
        const savePromise = new Promise((res, rej) => {
          saveRes = res;
          saveRej = rej;
        });
  
        this.$store.commit('openDialog', {
          title: this.$t('are-you-sure'),
          content: this.$t('bulk-save-events-msg'),
          confirmCallBack: saveRes,
          cancelCallBack: saveRej,
          cancelBtn: true,
        });
  
        await savePromise.catch();
  
        if (this.timetable.status != 2) {
          this.bulkSave();
          return;
        }
  
        this.$store.commit('openDialog', {
          title: this.$t('saving-event'),
          content: this.$t('notify-students-question'),
          cancelBtn: true,
          confirmCallBack: () => {
            this.notifyStudents = true;
            this.bulkSave().then(() => {
              this.notifyStudents = false;
            });
          },
          confirmBtnText: this.$t('notify'),
          cancelBtnText: this.$t('no'),
          cancelCallBack: this.bulkSave
        });
      },
      deleteEvent() {
        if (this.submitting) return;
        this.submitting = true;
  
        this.$store.commit('showBlockingSpinner');
  
        this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/delete`, {
          method: 'DELETE',
          data: { id: this.id },
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw new Error();
  
            this.$router.go(-1);
          })
          .catch(err => {
            console.error(err);
            this.$log.error.call(this, err);
            this.$store.commit('openDialog', {
              title: this.$t('something-went-wrong'),
              content: this.$t('error-deleting-event'),
            });
          })
          .finally(() => {
            this.submitting = false;
            this.$store.commit('hideBlockingSpinner');
          });
      },
      alertIfNoStudents() {
        if (!this.event.students.length) {
          this.$store.commit('openDialog', {
            title: this.$t('no-students'),
            content: this.$t('please-add-students-msg')
          });
          return true;
        }
        return false;
      },
      toggleUpdateId(id) {
        if (this.updateIds.includes(id)) {
          this.updateIds = this.updateIds.filter(el => el !== id);
        } else {
          this.updateIds = [...this.updateIds, id];
        }
      },
      condone(user_id , ttevent_id, undo, is_teacher = false){
        if(!(user_id && ttevent_id) ) return ;

        this.condoning = true;

        return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/condoneclash`, {
          method: 'POST',
          data: {
            user_id: user_id,
            ttevent_id: ttevent_id,
            undo: undo,
            is_teacher: is_teacher
          },
        })
          .then(res => {
            if (res.status !== 'SUCCESS') throw new Error();
            this.fetchClashes();
            this.fetchTeacherClashes()
          })
          .catch(err => {
            console.error(err);
            this.$store.commit('openDialog', {
              title: this.$t('something-went-wrong'),
              content: this.$t('error-condoning-clashes'),
            });
          })
          .finally(() => this.condoning = false);

      }
    },
    computed: {
        canCondone(){
            return this.$UserSession.hasPriv.call(this,'CONDONE_CLASHES');
        },
        isOutsideTimetableSpan() {
            if (!this.date || !this.timetable) return false;
            return this.date < this.timetable.datefrom || this.date > this.timetable.dateto;
        },
    }
  };
  </script>
  